import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Rocket, Clock, Target, Briefcase, Award, Gauge, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// One colour per journey stage so the rail reads as a spectrum, not six identical pills.
const STAGE_TINTS = {
  discover:  { border: 'border-cyan-300/60',    bg: 'bg-cyan-300/15',    dot: 'bg-cyan-300',    glow: 'shadow-[0_0_28px_-8px_rgba(34,211,238,0.65)]',  doneBorder: 'border-cyan-400/35',    doneBg: 'bg-cyan-400/10',    doneText: 'text-cyan-200',    hover: 'hover:bg-cyan-400/20',    hoverText: 'hover:text-cyan-200' },
  lifestyle: { border: 'border-violet-300/60',  bg: 'bg-violet-300/15',  dot: 'bg-violet-300',  glow: 'shadow-[0_0_28px_-8px_rgba(167,139,250,0.65)]', doneBorder: 'border-violet-400/35',  doneBg: 'bg-violet-400/10',  doneText: 'text-violet-200',  hover: 'hover:bg-violet-400/20',  hoverText: 'hover:text-violet-200' },
  explore:   { border: 'border-amber-300/60',   bg: 'bg-amber-300/15',   dot: 'bg-amber-300',   glow: 'shadow-[0_0_28px_-8px_rgba(251,191,36,0.6)]',   doneBorder: 'border-amber-400/35',   doneBg: 'bg-amber-400/10',   doneText: 'text-amber-200',   hover: 'hover:bg-amber-400/20',   hoverText: 'hover:text-amber-200' },
  decide:    { border: 'border-rose-300/60',    bg: 'bg-rose-300/15',    dot: 'bg-rose-300',    glow: 'shadow-[0_0_28px_-8px_rgba(251,113,133,0.6)]',  doneBorder: 'border-rose-400/35',    doneBg: 'bg-rose-400/10',    doneText: 'text-rose-200',    hover: 'hover:bg-rose-400/20',    hoverText: 'hover:text-rose-200' },
  plan:      { border: 'border-sky-300/60',     bg: 'bg-sky-300/15',     dot: 'bg-sky-300',     glow: 'shadow-[0_0_28px_-8px_rgba(125,211,252,0.6)]',  doneBorder: 'border-sky-400/35',     doneBg: 'bg-sky-400/10',     doneText: 'text-sky-200',     hover: 'hover:bg-sky-400/20',     hoverText: 'hover:text-sky-200' },
  launch:    { border: 'border-emerald-300/60', bg: 'bg-emerald-300/15', dot: 'bg-emerald-300', glow: 'shadow-[0_0_28px_-8px_rgba(52,211,153,0.6)]',   doneBorder: 'border-emerald-400/35', doneBg: 'bg-emerald-400/10', doneText: 'text-emerald-200', hover: 'hover:bg-emerald-400/20', hoverText: 'hover:text-emerald-200' },
};

const TILE_TINTS = [
  { card: 'glass-cyan',    icon: 'text-cyan-300' },
  { card: 'glass-violet',  icon: 'text-violet-300' },
  { card: 'glass-emerald', icon: 'text-emerald-300' },
  { card: 'glass-amber',   icon: 'text-amber-300' },
];

const StageRail = ({ stages }) => (
  <div className="flex items-center gap-1 overflow-x-auto pb-2" data-testid="mission-stage-rail">
    {stages.map((s, i) => {
      const tint = STAGE_TINTS[s.key] || STAGE_TINTS.discover;
      return (
        <div key={s.key} className="flex items-center flex-shrink-0">
          <Link to={s.href} data-testid={`stage-${s.key}`}
            className={`group flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium border transition-all duration-200 hover:-translate-y-0.5 ${
              s.status === 'done'
                ? `${tint.doneBorder} ${tint.doneBg} ${tint.doneText} ${tint.hover}`
                : s.status === 'current'
                  ? `${tint.border} ${tint.bg} text-white ${tint.glow}`
                  : `border-white/10 bg-white/[0.04] text-slate-300 ${tint.hoverText}`
            }`}>
            {s.status === 'done'
              ? <Check className="w-3.5 h-3.5" />
              : <span className={`h-1.5 w-1.5 rounded-full ${s.status === 'current' ? `${tint.dot} animate-pulse` : 'bg-slate-500'}`} />}
            {s.label}
          </Link>
          {i < stages.length - 1 && <span className="w-5 h-px bg-white/15 mx-0.5" />}
        </div>
      );
    })}
  </div>
);

const StatTile = ({ icon: Icon, value, label, href, testid, tint }) => (
  <Link to={href} data-testid={testid}
    className={`glass-tint ${tint.card} p-5 flex flex-col gap-1.5 group`}>
    <Icon className={`w-5 h-5 ${tint.icon} transition-transform duration-200 group-hover:scale-110`} />
    <p className="font-outfit text-2xl font-semibold text-white">{value}</p>
    <p className="text-xs text-slate-300/75">{label}</p>
  </Link>
);

const Skeleton = () => (
  <div className="space-y-6 animate-pulse" data-testid="mission-control-skeleton">
    <div>
      <div className="h-10 w-72 rounded-lg bg-white/[0.07]" />
      <div className="h-4 w-96 rounded bg-white/[0.05] mt-3" />
    </div>
    <div className="flex gap-2">
      {[...Array(6)].map((_, i) => <div key={i} className="h-10 w-28 rounded-full bg-white/[0.05]" />)}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 h-56 rounded-2xl bg-white/[0.05]" />
      <div className="h-56 rounded-2xl bg-white/[0.05]" />
    </div>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => <div key={i} className="h-28 rounded-2xl bg-white/[0.05]" />)}
    </div>
  </div>
);

export default function MissionControl() {
  const [data, setData] = useState(null);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    axios.get(`${API}/journey/mission-control`, getAuthHeader())
      .then((r) => setData(r.data))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!data) return <Skeleton />;
  const { stages, next_best_action: nba, resume, counts, launch_ready, deadlines } = data;

  return (
    <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
      className="space-y-6" data-testid="mission-control">
      <div>
        <p className="text-base text-slate-300/85">
          Here's where you are, and here's what matters next.
        </p>
      </div>

      <StageRail stages={stages} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className={`lg:col-span-2 glass-tint ${nba.urgent ? 'glass-amber' : 'glass-cyan'} p-7`} data-testid="next-best-action">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-cyan-300 inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Your next best action
          </span>
          <h2 className="font-outfit text-2xl font-semibold text-white mt-3">{nba.headline}</h2>
          <p className="text-base text-slate-200/85 mt-3 leading-relaxed">{nba.body}</p>
          <Link to={nba.href} data-testid="nba-cta"
            className="inline-flex items-center gap-2 mt-6 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 px-6 py-3 text-sm font-semibold text-white hover:gap-3 transition-all duration-200">
            {nba.cta} <ArrowRight className="w-4 h-4" />
          </Link>

          {resume && (
            <Link to={resume.href} data-testid="resume-link"
              className="flex items-center gap-2 mt-6 pt-5 border-t border-white/10 text-sm text-slate-300 hover:text-white transition-colors duration-200">
              <Clock className="w-4 h-4 text-cyan-300 flex-shrink-0" />
              <span className="flex-1">{resume.label}</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>

        <div className="space-y-5">
          {launch_ready && (
            <div className="glass-tint glass-emerald p-6" data-testid="launch-ready-card">
              <Rocket className="w-6 h-6 text-emerald-300" />
              <p className="font-outfit text-lg font-semibold text-white mt-3">Ready to launch</p>
              <p className="text-sm text-slate-300/80 mt-2">
                Your FuturePath is built. Start applications, scholarships and registrations.
              </p>
              <Link to="/app/launch" data-testid="launch-cta"
                className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-emerald-200 hover:gap-3 transition-all duration-200">
                Go to Launch <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}

          {deadlines?.length > 0 && (
            <div className="glass-tint glass-amber p-6" data-testid="deadlines-card">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-200">Upcoming deadlines</p>
              <ul className="mt-3 space-y-2.5">
                {deadlines.map((d) => (
                  <li key={d.title} className="flex items-start justify-between gap-3 text-sm">
                    <span className="text-slate-200/90 line-clamp-2">{d.title}</span>
                    <span className="text-amber-200 font-semibold whitespace-nowrap">{d.days}d</span>
                  </li>
                ))}
              </ul>
              <Link to="/app/scholarships" className="inline-flex items-center gap-2 mt-4 text-sm font-semibold text-amber-200 hover:gap-3 transition-all duration-200">
                See all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </div>

    </motion.section>
  );
}
